import { useMemo } from 'react';

const dummyFormalCriteriaByGradingRelevance = {
  false: {
    wordcount: 300,
    wordcountTolerance: 0.3,
  },
  true: {
    wordcount: 1000,
    wordcountTolerance: 0.2,
  },
};

/**
 * Transform module and task data for preview display
 *
 * This hook adapts data from the lecturer/admin format to the format
 * expected by the student UI components
 *
 * @param {Object} module - The module to preview
 * @param {Array} tasks - Tasks in the module
 * @param {Array} allModules - Optional list of all modules for context
 * @returns {Object} Transformed data ready for preview
 */
const useModulePreviewData = (module, tasks = [], allModules = []) => {
  // Transform module for preview
  const previewModule = useMemo(() => {
    if (!module) return null;

    return {
      ...module,
      // Add student-specific properties
      state: {
        studentWorkflowState: 'assigned',
        workflowState: 'published',
      },
      // Ensure course relationship is defined
      courses: module.courses?.length
        ? module.courses
        : [
          {
            id: 'preview-course-id',
            title: 'Preview Course',
          },
        ],
    };
  }, [module]);

  // Transform tasks for preview
  const previewTasks = useMemo(() => {
    if (!tasks.length) return [];

    const gradingSystemMap = {
      true: 'full_evaluation',
      false: 'pass_fail',
    };

    return tasks.map((task) => ({
      ...task,
      // Add student-specific properties
      submission: {
        ...task.submission,
        state: '',
        wordcount: task.evaluation?.formalCriteria?.wordcount || 0,
      },
      gradingSystem: gradingSystemMap[task.evaluation?.gradingRelevant] || 'pass_fail',
      // Ensure level is defined
      level: task.level || 1,
      // Add evaluation structure if missing
      evaluation: {
        ...task.evaluation,
        mode: task.evaluation?.mode || 'manual',
        gradingRelevant: task.evaluation?.gradingRelevant || false,
        defaultFormalCriteria: task.evaluation?.defaultFormalCriteria || false,
        formalCriteria:
          task.evaluation?.formalCriteria
          || dummyFormalCriteriaByGradingRelevance[task.evaluation?.gradingRelevant || false],
      },
      // Add empty resources array if missing
      resources: task.resources || [],
      // Ensure metadata is defined
      metadata: task.metadata || {},
    }));
  }, [tasks]);

  // Create mock user for preview
  const previewUser = {
    id: 'preview-user',
    email: 'preview@example.com',
    firstname: 'Preview',
    lastname: 'User',
    role: 'student',
  };

  // Prepare all modules for course view
  const previewModules = useMemo(() => {
    if (allModules.length > 0) {
      return allModules.map((m) => ({
        ...m,
        state: {
          studentWorkflowState: m.id === module?.id ? 'in_progress' : 'available',
          workflowState: 'published',
        },
        courses: m.courses?.length
          ? m.courses
          : [
            {
              id: 'preview-course-id',
              title: 'Preview Course',
            },
          ],
      }));
    }

    return previewModule ? [previewModule] : [];
  }, [previewModule, allModules, module]);

  // Create selected module info for AppStateStore replacement
  const selectedModuleInfo = useMemo(() => {
    if (!previewModule) return null;

    return {
      moduleId: previewModule.id,
      courseId: previewModule.courses[0].id,
    };
  }, [previewModule]);

  return {
    module: previewModule,
    tasks: previewTasks,
    user: previewUser,
    modules: previewModules,
    selectedModule: selectedModuleInfo,
  };
};

export default useModulePreviewData;
